import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const h = React.createElement;

// All document types known to the system. Keys must match `document_type` values
// accepted by POST /api/documents/upload.
export const DOC_TYPES = {
  PAC_CERTIFICATE: "PAC Certificate",
  TECHNICAL_SPECS: "Technical Specifications",
  JUSTIFICATION_NOTE: "Justification Note (Non-GeM)",
  COMPARATIVE_STATEMENT: "Comparative Statement",
  PROPRIETARY_JUSTIFICATION: "Proprietary / Single Tender Justification",
  QUOTATION: "Quotation / Price Bid",
  BOQ: "Bill of Quantities (BOQ)",
  OEM_QUOTES: "OEM Quotes (\u22653)",
  EMD_EXEMPTION_PROOF: "EMD Exemption Proof",
  PBG_DOCUMENT: "PBG / Performance Security Document",
  INTEGRITY_PACT: "Signed Integrity Pact",
  DC_TECH_SPEC: "Datacentre - Technical Specification",
  DC_RACK_LAYOUT: "Datacentre - Rack Layout Diagram",
  DC_POWER_SHEET: "Datacentre - Power Consumption Sheet",
  OTHER: "Other Supporting Document",
};

// Required + Optional documents per procurement mode + channel combination.
// Keys are derived from `procurement_channel + ":" + procurement_mode`.
// `*` matches any procurement_mode.
const CHECKLIST_RULES = [
  // GeM modes
  { match: ["GEM:DIRECT_PURCHASE"], required: [], optional: ["TECHNICAL_SPECS", "QUOTATION", "OTHER"] },
  { match: ["GEM:LPC", "GEM:OEM_COMPARISON"], required: ["TECHNICAL_SPECS"], optional: ["COMPARATIVE_STATEMENT", "QUOTATION", "OTHER"] },
  { match: ["GEM:TENDER"], required: ["BOQ", "TECHNICAL_SPECS"], optional: ["INTEGRITY_PACT", "EMD_EXEMPTION_PROOF", "OTHER"] },
  { match: ["GEM:PAC", "GEM:PAC_LPC"], required: ["PAC_CERTIFICATE", "TECHNICAL_SPECS", "PROPRIETARY_JUSTIFICATION"], optional: ["QUOTATION", "OTHER"] },
  // Non-GeM modes
  { match: ["NON_GEM:DIRECT_PURCHASE"], required: ["TECHNICAL_SPECS", "JUSTIFICATION_NOTE"], optional: ["QUOTATION", "PAC_CERTIFICATE", "OTHER"] },
  { match: ["NON_GEM:LPC", "NON_GEM:LOCAL_PURCHASE_COMMITTEE"], required: ["TECHNICAL_SPECS", "JUSTIFICATION_NOTE"], optional: ["COMPARATIVE_STATEMENT", "QUOTATION", "OTHER"] },
  { match: ["NON_GEM:OEM_COMPARISON"], required: ["TECHNICAL_SPECS", "JUSTIFICATION_NOTE", "OEM_QUOTES"], optional: ["COMPARATIVE_STATEMENT", "OTHER"] },
  { match: ["NON_GEM:TENDER", "NON_GEM:TENDER_ENQUIRY"], required: ["BOQ", "TECHNICAL_SPECS", "JUSTIFICATION_NOTE"], optional: ["INTEGRITY_PACT", "EMD_EXEMPTION_PROOF", "PBG_DOCUMENT", "OTHER"] },
  { match: ["NON_GEM:PAC", "NON_GEM:PAC_LPC"], required: ["PAC_CERTIFICATE", "TECHNICAL_SPECS", "PROPRIETARY_JUSTIFICATION", "JUSTIFICATION_NOTE"], optional: ["QUOTATION", "OTHER"] },
];

const FALLBACK = { required: ["TECHNICAL_SPECS"], optional: ["QUOTATION", "OTHER"] };

export function getChecklist(procurementChannel, procurementMode) {
  if (!procurementChannel || !procurementMode) return FALLBACK;
  const key = procurementChannel + ":" + procurementMode;
  for (const r of CHECKLIST_RULES) if (r.match.includes(key)) return r;
  return FALLBACK;
}

function DocumentChecklist({ requisitionId, procurementChannel, procurementMode, getAuthHeader, token, isViewMode }) {
  const [docs, setDocs] = useState([]);
  const [busyType, setBusyType] = useState(null);
  const fileRef = useRef(null);
  const pendingTypeRef = useRef(null);

  const checklist = getChecklist(procurementChannel, procurementMode);
  const allTypes = [...checklist.required.map(t => ({ value: t, required: true })), ...checklist.optional.map(t => ({ value: t, required: false }))];

  const refresh = async () => {
    if (!requisitionId) return;
    try {
      const r = await axios.get(API_URL + "/documents/by-requisition/" + requisitionId, getAuthHeader());
      setDocs(r.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [requisitionId]);

  const docsByType = docs.reduce((acc, d) => { (acc[d.document_type] = acc[d.document_type] || []).push(d); return acc; }, {});

  const triggerUpload = (type) => {
    if (!requisitionId) { toast.error("Save the requisition first to attach documents"); return; }
    pendingTypeRef.current = type;
    if (fileRef.current) { fileRef.current.value = ""; fileRef.current.click(); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    const type = pendingTypeRef.current;
    if (!file || !type || !requisitionId) return;
    setBusyType(type);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(
        API_URL + "/documents/upload?requisition_id=" + requisitionId + "&document_type=" + type,
        fd,
        { headers: { Authorization: "Bearer " + token, "Content-Type": "multipart/form-data" } }
      );
      toast.success(DOC_TYPES[type] + " uploaded");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusyType(null);
      pendingTypeRef.current = null;
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await axios.delete(API_URL + "/documents/" + docId, getAuthHeader());
      toast.success("Document removed");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const missingRequired = checklist.required.filter(t => !(docsByType[t] && docsByType[t].length));

  return h(Card, { className: "mb-6", "data-testid": "pr-documents-card" },
    h(CardHeader, { className: "border-b border-slate-100 bg-slate-50" },
      h("div", { className: "flex items-center justify-between" },
        h("div", null,
          h(CardTitle, { className: "text-lg flex items-center gap-2" },
            h("span", { className: "bg-slate-600 text-white text-xs px-2 py-1 rounded" }, "\u00A0\u00A0"),
            "Documents"
          ),
          h("p", { className: "text-sm text-slate-500 mt-1" },
            requisitionId
              ? (procurementChannel && procurementMode
                  ? "Required for " + procurementChannel.replace("_", " ") + " \u00B7 " + procurementMode.replace(/_/g, " ")
                  : "Procurement mode will determine the required document list")
              : "Save the requisition first \u2014 the upload section unlocks once a requisition number is generated"
          )
        ),
        missingRequired.length > 0 && h(Badge, { className: "bg-amber-100 text-amber-800 border border-amber-200" }, missingRequired.length + " required document" + (missingRequired.length === 1 ? "" : "s") + " pending")
      )
    ),
    h(CardContent, { className: "p-6 space-y-3" },
      h("input", { ref: fileRef, type: "file", className: "hidden", onChange: handleFile, "data-testid": "pr-doc-file-input" }),
      allTypes.length === 0 && h("p", { className: "text-sm text-slate-500" }, "No document expectations for this mode."),
      allTypes.map(({ value, required }) => {
        const list = docsByType[value] || [];
        const uploaded = list.length > 0;
        return h("div", {
          key: value,
          className: "flex items-start justify-between gap-4 p-3 rounded-lg border " + (uploaded ? "border-emerald-200 bg-emerald-50/40" : (required ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-white")),
          "data-testid": "pr-doc-row-" + value
        },
          h("div", { className: "flex-1 min-w-0" },
            h("div", { className: "flex items-center gap-2 flex-wrap" },
              h("span", { className: "text-sm font-medium text-slate-800" }, DOC_TYPES[value] || value),
              required && h("span", { className: "text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-700" }, "Required"),
              uploaded && h("span", { className: "text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" }, "\u2713 Uploaded")
            ),
            list.length > 0 && h("div", { className: "mt-2 space-y-1" },
              list.map(d => h("div", { key: d.id, className: "flex items-center justify-between text-xs text-slate-600 bg-white border border-slate-200 rounded px-2 py-1" },
                h("span", { className: "truncate flex-1" }, (d.filename || d.original_name || "document"), " \u00B7 ", new Date(d.uploaded_at || d.created_at).toLocaleDateString("en-IN")),
                !isViewMode && h(Button, {
                  type: "button", size: "xs", variant: "ghost",
                  className: "text-rose-600 hover:text-rose-700 h-6 px-2",
                  onClick: () => handleDelete(d.id),
                  "data-testid": "pr-doc-delete-" + d.id
                }, "Delete")
              ))
            )
          ),
          !isViewMode && h(Button, {
            type: "button", size: "sm", variant: uploaded ? "outline" : "default",
            disabled: !requisitionId || busyType === value,
            onClick: () => triggerUpload(value),
            "data-testid": "pr-doc-upload-" + value
          }, busyType === value ? "Uploading\u2026" : (uploaded ? "Replace / Add" : "Upload"))
        );
      })
    )
  );
}

export default DocumentChecklist;
